import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import {
  Shield, ArrowRight, Home, Backpack, ClipboardList, FileText,
  AlertTriangle, QrCode, Download, Printer, Plus,
  PawPrint, ArrowLeft, LogOut, Edit,
  Trash2, Eye, Calendar, ChevronRight,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const B = {
  border: `1px solid ${C.navy}15`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

function BCard({ children, className = "", style = {}, onClick, ...props }) {
  return (
    <div className={`bg-white ${className}`} style={{ border: B.border, boxShadow: B.shadow, borderRadius: 0, ...style }} onClick={onClick} {...props}>
      {children}
    </div>
  );
}

function BBadge({ children, bg = "#8B9A2D", color = "#fff" }) {
  return (
    <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
      style={{ fontFamily: B.mono, background: bg, color, border: "2px solid #2D3561" }}
    >{children}</span>
  );
}

const EMERGENCY_TYPES = [
  { id: "dana", label: "DANA", icon: "" },
  { id: "inundacion", label: "Inundación", icon: "" },
  { id: "incendio", label: "Incendio", icon: "" },
  { id: "terremoto", label: "Terremoto", icon: "" },
  { id: "huracan", label: "Huracán", icon: "" },
  { id: "sequia", label: "Sequía", icon: "" },
  { id: "nevada", label: "Nevada", icon: "" },
  { id: "ataque", label: "Ataque", icon: "" },
  { id: "abandono_masivo", label: "Abandono masivo", icon: "" },
  { id: "epidemia", label: "Epidemia", icon: "" },
  { id: "otro", label: "Otro", icon: "" },
];

export default function PreparaDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("inicio");
  const [plans, setPlans] = useState([]);
  const [domicileCards, setDomicileCards] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    loadData();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const token = localStorage.getItem("ea_token");
      const headers = { Authorization: `Bearer ${token}` };
      const [plansRes, cardsRes, checklistsRes] = await Promise.all([
        axios.get(`${API}/prepara/plans`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/prepara/domicile-cards`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/prepara/checklists`, { headers }).catch(() => ({ data: [] })),
      ]);
      setPlans(plansRes.data || []);
      setDomicileCards(cardsRes.data || []);
      setChecklists(checklistsRes.data || []);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally { setLoading(false); }
  };

  const handleLogout = () => { logout(); navigate("/"); };

  if (!user) return null;

  const TABS = [
    { id: "inicio", label: "INICIO" },
    { id: "planes", label: `PLANES (${plans.length})` },
    { id: "tarjetas", label: `TARJETAS (${domicileCards.length})` },
    { id: "checklists", label: `CHECKLISTS (${checklists.length})` },
  ];

  const QUICK_ACTIONS = [
    { icon: FileText, title: "Crear plan de emergencia", desc: "Nuevo plan personalizado", action: () => navigate("/prepara/plan"), bg: "#8B9A2D" },
    { icon: Home, title: "Nueva tarjeta de domicilio", desc: "Registra tus animales", action: () => navigate("/prepara/tarjeta"), bg: "#8B9A2D" },
    { icon: Backpack, title: "Kits de Emergencia", desc: "Casa, Evacuación, Primeros Auxilios", action: () => navigate("/prepara/mochila"), bg: "#8B9A2D" },
    { icon: FileText, title: "Mi Plan de Emergencia", desc: "Plan familiar personalizado", action: () => navigate("/prepara/mi-plan"), bg: "#686af3" },
    { icon: Calendar, title: "Calendario", desc: "Citas, vacunas y recordatorios", action: () => navigate("/prepara/calendario"), bg: "#a88efb" },
  ];

  return (
    <div className="min-h-screen" data-testid="prepara-dashboard" style={{ background: "#f8f7f4", fontFamily: B.font }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: "#2D3561", borderBottom: B.border }}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/")} className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:opacity-80"
                style={{ fontFamily: B.mono, color: "#f8f7f4" }}>
                <ArrowLeft className="w-4 h-4" /> HUB
              </button>
              <div className="h-5 w-px" style={{ background: "#f8f7f440" }} />
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 flex items-center justify-center" style={{ background: "#8B9A2D", border: "2px solid #f8f7f4" }}>
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-base font-bold" style={{ color: "#f8f7f4" }}>PREPARA</h1>
                  <p className="text-[9px] tracking-widest" style={{ fontFamily: B.mono, color: "#8B9A2D" }}>PANEL DE PREPARACIÓN</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs hidden sm:block" style={{ fontFamily: B.mono, color: "#f8f7f480" }}>
                {user.name || user.email}
              </span>
              <button onClick={() => navigate("/conecta")} className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider hover:opacity-80"
                style={{ fontFamily: B.mono, color: "#686af3", border: "2px solid #686af3" }}>
                CONECTA
              </button>
              <button onClick={handleLogout} className="w-8 h-8 flex items-center justify-center hover:opacity-80"
                style={{ border: "2px solid #e8637a" }}>
                <LogOut className="w-4 h-4" style={{ color: "#e8637a" }} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: "#2D3561" }}>
            HOLA, {(user.name?.split(" ")[0] || "PREPARADOR/A").toUpperCase()}!
          </h2>
          <p className="text-sm" style={{ fontFamily: B.mono, color: "#999" }}>
            Gestiona tus planes de emergencia, tarjetas de domicilio y checklists
          </p>
        </motion.div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {QUICK_ACTIONS.map((item, idx) => (
            <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}>
              <BCard className="p-5 cursor-pointer transition-all hover:-translate-y-1" style={{ boxShadow: B.shadowSm }}
                onClick={item.action} data-testid={`quick-action-${idx}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" style={{ background: item.bg, border: "2px solid #2D3561" }}>
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold mb-0.5" style={{ color: "#2D3561" }}>{item.title}</h3>
                    <p className="text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#999" }}>{item.desc}</p>
                  </div>
                  <Plus className="w-5 h-5 flex-shrink-0" style={{ color: item.bg }} />
                </div>
              </BCard>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-6 flex-wrap">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors"
              style={{
                fontFamily: B.mono,
                background: activeTab === tab.id ? "#2D3561" : "#fff",
                color: activeTab === tab.id ? "#f8f7f4" : "#2D3561",
                border: B.border,
                marginRight: "-3px",
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* INICIO TAB */}
        {activeTab === "inicio" && (
          <div className="space-y-6">
            {/* Emergency Types */}
            <BCard className="p-6">
              <h3 className="text-lg font-bold mb-1" style={{ color: "#2D3561" }}>TIPOS DE EMERGENCIA</h3>
              <p className="text-[10px] tracking-wider mb-4" style={{ fontFamily: B.mono, color: "#999" }}>SELECCIONA UN TIPO PARA CREAR UN PLAN PERSONALIZADO</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                {EMERGENCY_TYPES.slice(0, 6).map((type) => (
                  <button key={type.id} onClick={() => navigate(`/prepara/plan?type=${type.id}`)}
                    className="p-4 text-center transition-all hover:-translate-y-1"
                    style={{ border: "2px solid #2D3561", background: "#fff" }}>
                    <span className="text-2xl mb-2 block">{type.icon}</span>
                    <span className="text-xs font-bold" style={{ color: "#2D3561" }}>{type.label}</span>
                  </button>
                ))}
              </div>
              <button onClick={() => navigate("/prepara/plan")}
                className="w-full mt-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1 hover:opacity-80"
                style={{ fontFamily: B.mono, color: "#8B9A2D", border: "2px dashed #8B9A2D" }}>
                VER TODOS LOS TIPOS <ChevronRight className="w-4 h-4" />
              </button>
            </BCard>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "PLANES", value: plans.length, icon: FileText, bg: "#8B9A2D" },
                { label: "TARJETAS", value: domicileCards.length, icon: Home, bg: "#8B9A2D" },
                { label: "CHECKLISTS", value: checklists.length, icon: ClipboardList, bg: "#8B9A2D" },
                { label: "ANIMALES", value: domicileCards.reduce((acc, c) => acc + (c.animals?.length || 0), 0), icon: PawPrint, bg: "#e8637a" },
              ].map((stat) => (
                <BCard key={stat.label} className="p-4 text-center" style={{ boxShadow: B.shadowSm }}>
                  <stat.icon className="w-5 h-5 mx-auto mb-2" style={{ color: stat.bg }} />
                  <p className="text-2xl font-bold" style={{ color: "#2D3561" }}>{stat.value}</p>
                  <p className="text-[10px] tracking-widest font-bold" style={{ fontFamily: B.mono, color: stat.bg }}>{stat.label}</p>
                </BCard>
              ))}
            </div>

            {/* Tip */}
            <BCard className="p-6" style={{ background: "#8B9A2D10", boxShadow: "4px 4px 0px #8B9A2D" }}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ background: "#8B9A2D", border: "2px solid #2D3561" }}>
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold mb-1" style={{ color: "#2D3561" }}>CONSEJO DE PREPARACIÓN</h3>
                  <p className="text-sm" style={{ color: "#666" }}>
                    Revisa tu mochila de vida cada 3 meses para asegurarte de que la comida y medicamentos no hayan caducado.
                  </p>
                </div>
              </div>
            </BCard>
          </div>
        )}

        {/* PLANES TAB */}
        {activeTab === "planes" && (
          <>
            {plans.length === 0 ? (
              <BCard className="p-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: "#ccc" }} />
                <h3 className="text-lg font-bold mb-2" style={{ color: "#2D3561" }}>NO TIENES PLANES DE EMERGENCIA</h3>
                <p className="text-xs mb-6" style={{ fontFamily: B.mono, color: "#999" }}>Crea tu primer plan personalizado para estar preparado</p>
                <button onClick={() => navigate("/prepara/plan")}
                  className="px-6 py-2.5 font-bold text-sm uppercase tracking-wider transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
                  style={{ fontFamily: B.font, background: "#8B9A2D", color: "#fff", border: B.border, boxShadow: B.shadowSm }}>
                  <Plus className="w-4 h-4" /> CREAR PLAN
                </button>
              </BCard>
            ) : (
              <div className="space-y-4">
                {plans.map((plan) => (
                  <BCard key={plan._id || plan.id} className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-2xl">{EMERGENCY_TYPES.find(t => t.id === plan.emergency_type)?.icon || ""}</span>
                        <div>
                          <h3 className="font-bold" style={{ color: "#2D3561" }}>
                            {plan.name || `Plan ${EMERGENCY_TYPES.find(t => t.id === plan.emergency_type)?.label}`}
                          </h3>
                          <p className="text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#999" }}>
                            CREADO {new Date(plan.created_at).toLocaleDateString("es-ES")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => navigate(`/prepara/plan?type=${plan.emergency_type}`)} title="Ver"
                          className="w-8 h-8 flex items-center justify-center hover:bg-[#f8f7f4]" style={{ border: "2px solid #2D3561" }}>
                          <Eye className="w-4 h-4" style={{ color: "#2D3561" }} />
                        </button>
                        <button onClick={() => navigate(`/prepara/plan?type=${plan.emergency_type}`)} title="Editar"
                          className="w-8 h-8 flex items-center justify-center hover:bg-[#f8f7f4]" style={{ border: "2px solid #2D3561" }}>
                          <Edit className="w-4 h-4" style={{ color: "#2D3561" }} />
                        </button>
                        <button onClick={async () => {
                          if (window.confirm("¿Eliminar este plan?")) {
                            try {
                              await axios.delete(`${API}/prepara/plans/${plan.plan_id}`, { headers: { Authorization: `Bearer ${localStorage.getItem("ea_token")}` } });
                              toast.success("Plan eliminado");
                              loadData();
                            } catch { toast.error("Error al eliminar"); }
                          }
                        }} title="Eliminar"
                          className="w-8 h-8 flex items-center justify-center hover:bg-[#e8637a10]" style={{ border: "2px solid #e8637a" }}>
                          <Trash2 className="w-4 h-4" style={{ color: "#e8637a" }} />
                        </button>
                      </div>
                    </div>
                  </BCard>
                ))}
              </div>
            )}
          </>
        )}

        {/* TARJETAS TAB */}
        {activeTab === "tarjetas" && (
          <>
            {domicileCards.length === 0 ? (
              <BCard className="p-12 text-center">
                <Home className="w-12 h-12 mx-auto mb-4" style={{ color: "#ccc" }} />
                <h3 className="text-lg font-bold mb-2" style={{ color: "#2D3561" }}>NO TIENES TARJETAS DE DOMICILIO</h3>
                <p className="text-xs mb-6" style={{ fontFamily: B.mono, color: "#999" }}>Crea una tarjeta para alertar a los equipos de rescate</p>
                <button onClick={() => navigate("/prepara/tarjeta")}
                  className="px-6 py-2.5 font-bold text-sm uppercase tracking-wider transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
                  style={{ fontFamily: B.font, background: "#8B9A2D", color: "#fff", border: B.border, boxShadow: B.shadowSm }}>
                  <Plus className="w-4 h-4" /> CREAR TARJETA
                </button>
              </BCard>
            ) : (
              <div className="space-y-4">
                {domicileCards.map((card) => (
                  <BCard key={card._id || card.id} className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 flex items-center justify-center" style={{ background: "#8B9A2D10", border: "2px solid #2D3561" }}>
                          <Home className="w-6 h-6" style={{ color: "#8B9A2D" }} />
                        </div>
                        <div>
                          <h3 className="font-bold" style={{ color: "#2D3561" }}>{card.address || "Mi domicilio"}</h3>
                          <p className="text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#999" }}>
                            {card.animals?.length || 0} ANIMALES REGISTRADOS
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {[QrCode, Download, Printer].map((Icon, i) => (
                          <button key={i} className="w-8 h-8 flex items-center justify-center hover:bg-[#f8f7f4]" style={{ border: "2px solid #2D3561" }}>
                            <Icon className="w-4 h-4" style={{ color: "#2D3561" }} />
                          </button>
                        ))}
                      </div>
                    </div>
                  </BCard>
                ))}
              </div>
            )}
          </>
        )}

        {/* CHECKLISTS TAB */}
        {activeTab === "checklists" && (
          <>
            {checklists.length === 0 ? (
              <BCard className="p-12 text-center">
                <Backpack className="w-12 h-12 mx-auto mb-4" style={{ color: "#ccc" }} />
                <h3 className="text-lg font-bold mb-2" style={{ color: "#2D3561" }}>NO TIENES CHECKLISTS</h3>
                <p className="text-xs mb-6" style={{ fontFamily: B.mono, color: "#999" }}>Crea un checklist para tener todo preparado</p>
                <button onClick={() => navigate("/prepara/mochila")}
                  className="px-6 py-2.5 font-bold text-sm uppercase tracking-wider transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
                  style={{ fontFamily: B.font, background: "#8B9A2D", color: "#fff", border: B.border, boxShadow: B.shadowSm }}>
                  <Plus className="w-4 h-4" /> CREAR CHECKLIST
                </button>
              </BCard>
            ) : (
              <div className="space-y-4">
                {checklists.map((checklist) => (
                  <BCard key={checklist._id || checklist.id} className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 flex items-center justify-center" style={{ background: "#8B9A2D10", border: "2px solid #2D3561" }}>
                          <Backpack className="w-6 h-6" style={{ color: "#8B9A2D" }} />
                        </div>
                        <div>
                          <h3 className="font-bold" style={{ color: "#2D3561" }}>{checklist.name || "Mochila de vida"}</h3>
                          <p className="text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#999" }}>
                            {checklist.items_completed || 0}/{checklist.items_total || 0} ITEMS PREPARADOS
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => navigate("/prepara/mochila")} title="Ver"
                          className="w-8 h-8 flex items-center justify-center hover:bg-[#f8f7f4]" style={{ border: "2px solid #2D3561" }}>
                          <Eye className="w-4 h-4" style={{ color: "#2D3561" }} />
                        </button>
                        <button onClick={() => navigate("/prepara/mochila")} title="Editar"
                          className="w-8 h-8 flex items-center justify-center hover:bg-[#f8f7f4]" style={{ border: "2px solid #2D3561" }}>
                          <Edit className="w-4 h-4" style={{ color: "#2D3561" }} />
                        </button>
                        <button onClick={async () => {
                          if (window.confirm("¿Eliminar este kit?")) {
                            try {
                              await axios.delete(`${API}/prepara/checklists/${checklist.checklist_id}`, { headers: { Authorization: `Bearer ${localStorage.getItem("ea_token")}` } });
                              toast.success("Kit eliminado");
                              loadData();
                            } catch { toast.error("Error al eliminar"); }
                          }
                        }} title="Eliminar"
                          className="w-8 h-8 flex items-center justify-center hover:bg-[#e8637a10]" style={{ border: "2px solid #e8637a" }}>
                          <Trash2 className="w-4 h-4" style={{ color: "#e8637a" }} />
                        </button>
                      </div>
                    </div>
                  </BCard>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
